import getDeliveryAvailableDateAPI from './api/getAvailableDate'
import { IGetDeliveryAvailableDateRequest } from './interfaces/IGetAvailableDateRequest'
import {
  CalendarExceptions,
  DeliveryException,
  FetchCalendarExceptionsParams
} from '@/store/modules/deliveryDates/interfaces/calendarException'
import { fetchExceptions } from '@/store/modules/deliveryDates/api/getCalendarExceptions'
import {
  filterCalendarExceptions,
  filterFutureExceptions,
  isRelevantException,
  processDeliveryExceptions
} from '@/utils/deliveryDate'
import { IGetDeliveryAvailableDateResponse } from '@/store/modules/deliveryDates/interfaces/IGetAvailableDateResponse'
import { DeliveryDatesMutationTypes } from '@/store/modules/deliveryDates/interfaces/mutation-types'
import { DeliveryDatesActionTypes } from '@/store/modules/deliveryDates/interfaces/action-types'
import { IDeliveryDatesState } from '@/store/modules/deliveryDates/interfaces/state'
import { IDeliveryDatesMutations } from '@/store/modules/deliveryDates/interfaces/IDeliveryDatesMutation'
import { IDeliveryDatesActions } from '@/store/modules/deliveryDates/interfaces/IDeliveryDatesAction'
import { TransportationTypes } from '@/utils/transportTypes'

const state = (): IDeliveryDatesState => ({
  availableDays: [],
  filteredDeliveryDates: [],
  unavailableDates: [],
  scheduleDates: [],
  availableExceptions: [],
  notificationExceptions: []
})

const getters = {}

const mutations: IDeliveryDatesMutations = {
  [DeliveryDatesMutationTypes.SET_AVAILABLE_DAYS] (state, payload: string[]) {
    state.availableDays = payload
  },
  [DeliveryDatesMutationTypes.SET_FILTERED_AVAILABLE_DAYS] (state, payload: string[]) {
    state.filteredDeliveryDates = payload
  },
  [DeliveryDatesMutationTypes.SET_UNAVAILABLE_DATES] (state, payload: string[]) {
    state.unavailableDates = payload
  },
  [DeliveryDatesMutationTypes.SET_SCHEDULE_DATES] (state, payload: string[]) {
    state.scheduleDates = payload
  },
  [DeliveryDatesMutationTypes.SET_AVAILABLE_EXCEPTIONS] (state, payload: DeliveryException[]) {
    state.availableExceptions = payload
  },
  [DeliveryDatesMutationTypes.SET_NOTIFICATION_EXCEPTIONS] (state, payload: CalendarExceptions[]) {
    state.notificationExceptions = payload
  }
}

const actions: IDeliveryDatesActions = {
  [DeliveryDatesActionTypes.GET_DELIVERY_AVAILABLE_DATE]: async ({ commit }, dto: IGetDeliveryAvailableDateRequest) => {
    const res: IGetDeliveryAvailableDateResponse = await getDeliveryAvailableDateAPI(dto)

    const deliveryExceptions = res?.deliveryExceptions || []
    const allDatesFromApi = res?.allDeliveryDate || []
    const filteredDatesFromApi = res?.filteredDeliveryDate || []

    const {
      allDates,
      filteredDates,
      unavailableDates,
      scheduleDates
    } = processDeliveryExceptions(
      deliveryExceptions,
      dto.shipmentId as TransportationTypes,
      allDatesFromApi,
      filteredDatesFromApi
    )

    const availableExceptions = filterFutureExceptions(deliveryExceptions)
      .filter((exception) =>
        isRelevantException(exception, dto.shipmentId as TransportationTypes))

    commit(DeliveryDatesMutationTypes.SET_AVAILABLE_DAYS, allDates)
    commit(DeliveryDatesMutationTypes.SET_FILTERED_AVAILABLE_DAYS, filteredDates)
    commit(DeliveryDatesMutationTypes.SET_UNAVAILABLE_DATES, unavailableDates)
    commit(DeliveryDatesMutationTypes.SET_SCHEDULE_DATES, scheduleDates)
    commit(DeliveryDatesMutationTypes.SET_AVAILABLE_EXCEPTIONS, availableExceptions)
  },

  async [DeliveryDatesActionTypes.FETCH_NOTIFICATION_EXCEPTION] ({ commit }, params: FetchCalendarExceptionsParams) {
    const data = await fetchExceptions(params.guid, params.shipTo, params.store)
    if (data) {
      const filteredData = filterCalendarExceptions(data)
      commit(DeliveryDatesMutationTypes.SET_NOTIFICATION_EXCEPTIONS, filteredData)
    }
  },
  async [DeliveryDatesActionTypes.SET_FETCHED_NOTIFICATION_EXCEPTION] ({ commit }, data: CalendarExceptions[]) {
    commit(DeliveryDatesMutationTypes.SET_NOTIFICATION_EXCEPTIONS, data)
  }
}

const DeliveryDatesModule = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default DeliveryDatesModule
